import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

/** A billing address resolved from a geocoder suggestion. `country_code` is the
 * ISO-3166-1 alpha-2 value Stripe needs; `country` stays the display name. */
export interface ResolvedAddress {
  street: string;
  number: string;
  zip: string;
  city: string;
  region: string;
  country: string;
  country_code: string;
}

export interface AddressSuggestion extends ResolvedAddress {
  /** Single-line rendering shown in the dropdown. */
  label: string;
}

interface PhotonFeature {
  properties?: {
    name?: string;
    street?: string;
    housenumber?: string;
    postcode?: string;
    city?: string;
    district?: string;
    state?: string;
    country?: string;
    countrycode?: string;
  };
}

/**
 * Address autocomplete against Photon (https://photon.komoot.io) — the OSM
 * geocoder Komoot runs specifically for as-you-type search.
 *
 * Why not Nominatim: its usage policy explicitly forbids autocomplete and caps
 * requests at 1/s. Photon is built for exactly this and needs no API key.
 *
 * Privacy note: the partial address a user types leaves our infrastructure and
 * reaches Komoot. Only what is typed into the search box is sent — never the
 * saved record, the client id, or anything else identifying the organisation.
 * Swap PHOTON_ENDPOINT for a self-hosted instance if that ever needs to stop.
 */
@Injectable({ providedIn: 'root' })
export class AddressLookupService {
  private static readonly PHOTON_ENDPOINT = 'https://photon.komoot.io/api/';

  /** Photon only localizes results into these; anything else falls back to en. */
  private static readonly SUPPORTED_LANGS = ['de', 'en', 'fr', 'it'];

  constructor(private http: HttpClient) {}

  search(query: string, lang = 'de'): Observable<AddressSuggestion[]> {
    const q = (query || '').trim();
    // Below three characters Photon returns mostly noise and we would burn a
    // request on every keystroke of a postcode.
    if (q.length < 3) {
      return of([]);
    }

    const params = new HttpParams()
      .set('q', q)
      .set('limit', '6')
      .set('lang', AddressLookupService.SUPPORTED_LANGS.includes(lang) ? lang : 'en')
      // Restrict to things that can actually be a billing address. Without this
      // the top hits are countries and regions.
      .append('layer', 'house')
      .append('layer', 'street');

    return this.http
      .get<{ features?: PhotonFeature[] }>(AddressLookupService.PHOTON_ENDPOINT, {
        params,
      })
      .pipe(
        map((res) => (res?.features || []).map((f) => this.toSuggestion(f))),
        // Dedupe: Photon happily returns the same street twice from different
        // OSM objects.
        map((list) =>
          list.filter(
            (s, i, all) => s.label && all.findIndex((o) => o.label === s.label) === i
          )
        ),
        // A geocoder outage must never break the form — the user can still type
        // the address by hand.
        catchError(() => of([]))
      );
  }

  private toSuggestion(feature: PhotonFeature): AddressSuggestion {
    const p = feature.properties || {};
    // For a `street` layer hit the street sits in `name`; for a `house` hit it
    // sits in `street` and `name` holds the building/POI name.
    const street = p.street || p.name || '';
    const city = p.city || p.district || '';
    const resolved: ResolvedAddress = {
      street,
      number: p.housenumber || '',
      zip: p.postcode || '',
      city,
      region: p.state || '',
      country: p.country || '',
      country_code: (p.countrycode || '').toUpperCase(),
    };

    const streetLine = [resolved.street, resolved.number]
      .filter((v) => !!v)
      .join(' ');
    const cityLine = [resolved.zip, resolved.city].filter((v) => !!v).join(' ');
    const label = [streetLine, cityLine, resolved.country]
      .filter((v) => !!v)
      .join(', ');

    return { ...resolved, label };
  }
}
